import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'

// UI
import { Box, Button, Flex, Heading, Spacer, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'

// Component

import { authSelector } from '@/stores/auth/authSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'

//API

import useUser from '@/features/users/useUser'
import useOfferPR from '@/features/offers/hooks/useOfferPR'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { DateCell, PriceCell, RawTextCell, RowSelectionCell } from '@/components/Table/components/Cell'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import useOfferList from '@/features/offers/hooks/useOfferList'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { ApprovalInvitationStatus, PRApprovalStatus, PRApprovalType, PRStatus } from '@eapproval/offer-shared'
import { ApprovalStatus } from '../../constants'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { createExcelFromAoA } from '@/utils/fileHelper'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

const PR_APPROVAL_INVITEE_ROLE = 'PR Approval Invitee'

const LOGISTIC_TYPE_OPTIONS = ['Create Master', 'Fill PO'].map((v) => ({ label: v, value: v }))

const OfferPRApprovalListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const approvalStatus = searchParams.get('approvalStatus')
  const PRStatusSearchParams = searchParams.get('PRStatus')
  const actionUser = searchParams.get('actionUser')

  const initialFilter = useMemo(() => {
    const columnFilters = []
    if (approvalStatus) {
      columnFilters.push({ id: 'approvalStatus', value: approvalStatus.split(',') })
    }

    if (PRStatusSearchParams) {
      columnFilters.push({ id: 'PRStatus', value: PRStatusSearchParams.split(',') })
    }

    if (actionUser) {
      columnFilters.push({ id: 'actionUser', value: actionUser.split(',') })
    }

    if (columnFilters.length === 0) {
      return {}
    }

    return { columnFilters: columnFilters }
  }, [approvalStatus, PRStatusSearchParams, actionUser])

  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerPRApproval`, {})

  const query = convertTableStateToQuery(storedTableState)
  // Hooks

  const { useFetchPRApprovals } = useOfferList()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()
  const { useBatchApprovePR } = useOfferPR()
  const batchApprovePR = useBatchApprovePR()
  const { open: openConfirmationModal } = useConfirmationModalManagement()
  const queryClient = useQueryClient()

  const { data: offerPRApprovalResponse, isRefetching } = useFetchPRApprovals(query)
  const downloadQuery = useMemo(() => ({ ...query, page: 1, pageSize: 0 }), [query])
  const { refetch: refetchAllPRApprovals } = useFetchPRApprovals(downloadQuery, {
    enabled: false,
    staleTime: Infinity,
  })

  //Options
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })
  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'simple' })
  const { data: GCKOptions } = useGCKCodeOptions({ status: 'active', labelType: 'detail' })
  const { data: allUserOptions } = useUserOptions({ status: 'active' })

  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: PMOptions } = useUserOptions({
    status: 'active',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    const offerId = row?.original?.offerId || row?.original?._id
    if (!offerId) {
      return
    }

    const params = new URLSearchParams()
    if (row?.original?.costBudgetIndex !== undefined) {
      params.set('PRCostBudgetIndex', String(row.original.costBudgetIndex))
    }
    if (row?.original?.PRVendorSelectionIndex !== undefined) {
      params.set('PRVendorSelectionIndex', String(row.original.PRVendorSelectionIndex))
    }

    const query = params.toString()
    navigate(`/offers/${offerId}${query ? `?${query}` : ''}`)
  }

  const selectedPRDetails = useMemo(() => {
    const rows = offerPRApprovalResponse?.data?.data
    if (!Array.isArray(rows)) {
      return []
    }
    return Object.keys(storedTableState?.rowSelection ?? {})
      .map((index) => rows[index])
      .filter(Boolean)
      .map((row) => ({
        offerId: row.offerId,
        costBudgetIndex: row.costBudgetIndex,
        vendorSelectionIndex: row.PRVendorSelectionIndex,
        submissionIndex: row.PRVendorSelectionSubmissionIndex,
      }))
  }, [offerPRApprovalResponse?.data?.data, storedTableState?.rowSelection])

  const handleBatchApprove = async () => {
    if (selectedPRDetails.length === 0) {
      toast.error('You have not selected any valid approvals.')
      return
    }

    const { isConfirmed } = await openConfirmationModal({
      title: 'Confirm Batch Approve',
      message: `Are you sure you want to approve ${selectedPRDetails.length} selected PR submission(s)?`,
    })

    if (!isConfirmed) {
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const { data: batchApprovalResult } = await batchApprovePR.mutateAsync(selectedPRDetails)
      queryClient.invalidateQueries(['offers', 'list'])

      if (Array.isArray(batchApprovalResult)) {
        const toastFn = batchApprovalResult.some((result) => !result.success) ? toast.error : toast.success
        toastFn(
          <Flex direction={'column'}>
            {batchApprovalResult.map((result, index) => (
              <Text key={index}>{`[${index + 1}/${batchApprovalResult.length}] ${result.remark}`}</Text>
            ))}
          </Flex>
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      setStoredTableState((prev) => ({ ...prev, rowSelection: {} }))
      dispatch(hideLoadingOverlay())
    }
  }

  const defaultColumns = [
    columnHelper.display({
      id: 'select',
      header: () => <></>,
      size: 50,
      cell: RowSelectionCell,
    }),

    columnHelper.accessor('PRNumber', {
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('raiser', {
      header: () => 'Raiser',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('requestor', {
      header: () => 'Requestor',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('requestDate', {
      header: () => 'Request Date',
      cell: DateCell,
      meta: { filterVariant: FilterType.DATE },
    }),
    columnHelper.accessor('remarks', {
      header: () => 'Remarks',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('PONumber', {
      header: () => 'PO #',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('licenseRequired', {
      header: () => 'Licensed Required',
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('expectedDeliveryDate', {
      header: () => 'Expected Delivery Date',
      cell: DateCell,
      meta: { filterVariant: FilterType.DATE },
    }),
    columnHelper.accessor('deliveryInstructions', {
      header: () => 'Delivery Instruction',
      meta: { filterVariant: FilterType.TEXT },
    }),

    columnHelper.accessor('subcontractorAgreementRequired', {
      header: () => 'Subcontractor Agreement Required',
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('subcontractorAgreementUploaded', {
      header: () => 'Subcontractor Agreement Uploaded',
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('SAPNumber', {
      header: () => 'SO# / SAP PR#',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('approvalType', {
      header: () => 'Approval Type',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(PRApprovalType).map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('actionUser', {
      header: () => 'Action User',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('approvalUser', {
      header: () => 'Approval User',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('logisticType', {
      header: () => 'Logistic Type',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: LOGISTIC_TYPE_OPTIONS,
      },
    }),
    columnHelper.accessor('WBS', {
      header: () => 'WBS',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('customer', {
      header: () => 'Customer',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('vendorName', {
      header: () => 'Vendor Name',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('projectManager', {
      header: () => 'Project Manager',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('projectEngineer', {
      header: () => 'Project Engineer',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('approvalRole', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [...Object.values(PRApprovalType), PR_APPROVAL_INVITEE_ROLE].map((v) => ({
          label: v,
          value: v,
        })),
      },
    }),
    columnHelper.accessor('approvalStatus', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          ...Object.values(PRApprovalStatus),
          ApprovalInvitationStatus.INVITED,
          ApprovalInvitationStatus.REPLIED,
        ].map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('approvalDueDate', {
      cell: DateCell,
      meta: { filterVariant: FilterType.DATE },
    }),
    columnHelper.accessor('PRStatus', {
      header: () => 'PR Status',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(PRStatus).map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('PRAmount', {
      header: () => 'PR Amount',
      cell: PriceCell,
      meta: { filterVariant: FilterType.RANGE },
    }),
  ]

  useEffect(() => {
    setStoredTableState((prev) => ({
      ...prev,
      rowSelection: {},
    }))
  }, [storedTableState?.pagination?.pageIndex])

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => {
        return {
          ...prev,
          ...initialFilter,
        }
      })

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter])

  const checkCanSelectRow = (row) => {
    return (
      !['invitation', 'quotationInvitation', 'bookingInvitation'].includes(row?.original?.approvalType) &&
      row?.original?.approvalStatus === ApprovalStatus.REQUIRED
    )
  }

  const handleDownload = async () => {
    try {
      dispatch(showLoadingOverlay())

      const { data: response } = await refetchAllPRApprovals()
      console.log('response', response)
      const downloadRows = response?.data?.data || []

      if (!Array.isArray(downloadRows) || downloadRows.length === 0) {
        toast.info('No data available for download.')
        return
      }

      const headers = [
        'PR Number',
        'Raiser',
        'Requestor',
        'Request Date',
        'Remarks',
        'PO #',
        'Licensed Required',
        'Expected Delivery Date',
        'Delivery Instruction',
        'Internal Document',
        'Logistic Informed',
        'Subcontractor Agreement Required',
        'SO# / SAP PR#',
        'Approval Type',
        'Approval User',
        'Logistic Type',
        'WBS',
        'Customer',
        'Vendor Name',
        'Project Manager',
        'Project Engineer',
        'Approval Role',
        'Approval Status',
        'Approval Due Date',
        'PR Status',
        'PR Amount',
      ]

      const dataRows = downloadRows.map((row) => [
        row?.PRNumber ?? '',
        row?.raiser ?? '',
        row?.requestor ?? '',
        row?.requestDate ?? '',
        row?.remarks ?? '',
        row?.PONumber ?? '',
        row?.licenseRequired ?? '',
        row?.expectedDeliveryDate ?? '',
        row?.deliveryInstructions ?? '',
        row?.subcontractorAgreementRequired ?? '',
        row?.subcontractorAgreementUploaded ?? '',
        row?.SAPNumber ?? '',
        row?.approvalType ?? '',
        row?.approvalUser ?? '',
        row?.actionUser ?? '',
        row?.logisticType ?? '',
        row?.WBS ?? '',
        row?.customer ?? '',
        row?.vendorName ?? '',
        row?.projectManager ?? '',
        row?.projectEngineer ?? '',
        row?.approvalRole ?? '',
        row?.approvalStatus ?? '',
        row?.approvalDueDate ?? '',
        row?.PRStatus ?? '',
        row?.PRAmount ?? '',
      ])

      const excelBlob = createExcelFromAoA([headers, ...dataRows])
      saveAs(excelBlob, 'PR Approvals.xlsx')
    } catch (error) {
      console.error(error)
      toast.error('Failed to download PR approvals.')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>PR Approvals ({offerPRApprovalResponse?.data.count ?? 0})</Heading>
          <Spacer />

          <Flex gap={1} alignItems={'center'}>
            <Button colorScheme={'green'} onClick={handleDownload}>
              Download
            </Button>
            <Button colorScheme={'blue'} onClick={handleBatchApprove}>
              Batch Approve
            </Button>
          </Flex>
        </Flex>
        <Box p={5} overflow={'scroll'}>
          <FullTable
            data={offerPRApprovalResponse?.data.data ? offerPRApprovalResponse.data.data : []}
            rowCount={offerPRApprovalResponse?.data.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleGotoOffer}
            enableRowSelection={checkCanSelectRow}
            isLoading={isRefetching}
          />
        </Box>
      </Box>
    </>
  )
}

export default OfferPRApprovalListPage
